import { useState, type FormEvent } from 'react';
import { dramaCategories } from '@/mocks/dramas';

const FORM_ID = 'dah8llroh653ivfvnod0';
const FORM_ACTION = 'https://readdy.ai/api/form/dah8llroh653ivfvnod0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function SubscribeForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const trap = String(formData.get('company_alt') ?? '').trim();
    if (trap) {
      setStatus('success');
      return;
    }

    if (formData.get('company_alt') !== null) formData.delete('company_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(
          isSpam
            ? '提交未通过校验，请检查后重试。'
            : serverMsg || '提交失败，请稍后重试。'
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，未能提交，请稍后重试。');
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="relative rounded-lg overflow-hidden border border-background-200 bg-background-100">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-6 md:p-10">
              <p className="font-label text-xs tracking-[0.25em] text-primary-600 mb-3">
                WEEKLY BRIEF / 订阅
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">
                每周一封「对照简报」
              </h2>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                新归档的爆料短剧、更新过的改编判定，以及当周片尾声明的措辞变化，我们整理成一封短信发给你。不发广告，随时可退订。
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-foreground-600">
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-secondary-600"></i>
                  每周四发送，只发一条
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-secondary-600"></i>
                  含当周新增档案与评分变化
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-secondary-600"></i>
                  邮箱仅用于发送简报
                </li>
              </ul>
            </div>

            <div className="p-6 md:p-10 bg-background-50 border-t lg:border-t-0 lg:border-l border-background-200">
              {status === 'success' ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-8">
                  <span className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-mail-check-line text-2xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                    订阅成功
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    下一封对照简报将在周四发出，请留意邮箱。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-5 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    再订阅一个邮箱
                  </button>
                </div>
              ) : (
                <form
                  id={`form-${FORM_ID}`}
                  data-readdy-form
                  onSubmit={handleSubmit}
                  noValidate
                  className="h-full flex flex-col"
                >
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="sub-email" className="block text-sm text-foreground-800 mb-2">
                        接收邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="sub-email"
                        type="email"
                        name="email"
                        required
                        placeholder="you@example.com"
                        className="w-full px-3.5 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="sub-interest" className="block text-sm text-foreground-800 mb-2">
                        最关心的题材
                      </label>
                      <select
                        id="sub-interest"
                        name="interest"
                        defaultValue={dramaCategories[0]}
                        className="w-full px-3.5 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                      >
                        {dramaCategories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="sub-note" className="block text-sm text-foreground-800 mb-2">
                        想优先看到的剧（选填）
                      </label>
                      <textarea
                        id="sub-note"
                        name="note"
                        rows={3}
                        maxLength={500}
                        placeholder="例如：想尽快看到反诈题材的对照档案"
                        className="w-full px-3.5 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors resize-none"
                      ></textarea>
                    </div>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    className="form-verify-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="mt-6 w-full px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {status === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base"></i>
                        提交中
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-base"></i>
                        订阅对照简报
                      </>
                    )}
                  </button>

                  {status === 'error' && (
                    <p role="alert" className="mt-3 text-xs text-primary-700">
                      {formError}
                    </p>
                  )}
                  <p className="mt-3 text-xs text-foreground-400 leading-relaxed">
                    提交即表示同意我们仅将邮箱用于发送本站简报，不会用于其他用途。
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}